// Control log (design/screens/control-log.html): every PIN-authorised action with Refused and Locked rows as first-class
// evidence — the totals strip and the by-user table from `?totals=true`, filters, outcome chips, keyset paging and the CSV export
// through the signed link (TEN-53). Rows are the contract's ControlLogEntryView (packages/contracts/src/governance.ts): the acting
// and authorising user refs, the Control Id entered, the money columns and the outcome. A 404 is an empty state, a 403 the
// no-access panel. Access is decided by the session's permissions; the by-designation grant to Branch owners is the API's.
import { CONTROL_LOG_ROW_ACTIONS, CONTROL_OUTCOMES, controlIdDisplay, type ControlLogEntryView, type ControlLogRowAction, type ControlLogTotalsLine, type ControlOutcome } from "@visionx/contracts";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { governance, users, type ControlLogFilter } from "../api/endpoints";
import { describeError, isApiError } from "../api/problem";
import { startDownload } from "../lib/download";
import { addDays, formatDate, formatDateTime, formatMoney, formatTime, localDate, localDayWindow, percentFromBp } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { useWritesEnabled } from "../shell/OfflineBanner";
import { Alert, Chip, Empty, ErrorAlert, Loading, Pager, Panel, Select, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { NoAccessPanel, PageHead, rolesSentence } from "./common";

/** The action as attempted — it reads the same on an Authorised, Refused or Locked row. */
const ACTION_LABEL: Record<ControlLogRowAction, string> = {
  Delete: "Delete", Void: "Void", Discount: "Discount", PriceOverride: "Price override", CreditNote: "Credit note", Refund: "Refund",
  StockAdjustment: "Stock adjustment", CashUpVariance: "Cash-up variance", CashUpReopen: "Cash-up reopened", InvoiceEdit: "Invoice edit",
  MedicalAidOverride: "Medical-aid override", LabChange: "Lab change", PatientProhibit: "Patient prohibited",
  BranchDeactivate: "Deactivate branch", BranchReactivate: "Reactivate branch", UserDeactivate: "Deactivate user", UserReactivate: "Reactivate user",
  PinReset: "Reset PIN", ForcePasswordReset: "Force password reset", PinProtectedListChange: "PIN-protected list", PrivacyPublish: "Privacy notice published",
  PatientMerge: "Patient merged", HandoverExit: "Kiosk exit",
  // W11: a finalised stock take or the opening stock — FIXTURE §6's word for the row, beside "Stock adjustment" (TC-GOVERNANCE-136).
  StockTakeFinalise: "Stock-take variance", Other: "Other",
};
const ACCENT: ReadonlySet<ControlLogRowAction> = new Set(["Void", "CreditNote", "PriceOverride", "Delete"]);
const DEFAULT_DAYS = 30;
/** A user doing more than half of at least this many discounts is flagged in the by-user table, as the prototype does. */
const SHARE_FLAG_MIN_DISCOUNTS = 5;

type DayFilter = Omit<ControlLogFilter, "from" | "to" | "outcome"> & { from?: string | undefined; to?: string | undefined };

export function ControlLog() {
  const s = useSessionView();
  const online = useWritesEnabled();
  const today = localDate(new Date());
  // A Branch Owner reads the log of the branches they own only (D39): the branches offered are those, "All" means all of
  // them, and the page opens on the active branch only when it is one — another branch would answer 403 and the page would
  // open on the no-access panel (CLAUDE.md: the PWA never offers an organisation-wide action on a branch-bound grant).
  const owned = s?.branchScopedPermissions.find((p) => p.code === "governance.control_log.read")?.branchIds ?? null;
  const branches = (s?.switchableBranches ?? []).filter((b) => owned === null || owned.includes(b.id as never));
  const opening = s?.activeBranchId && (owned === null || owned.includes(s.activeBranchId as never)) ? s.activeBranchId : undefined;
  const [f, setF] = useState<DayFilter>({ from: addDays(today, -DEFAULT_DAYS), to: today, branchId: opening });
  const [outcomes, setOutcomes] = useState<ReadonlySet<ControlOutcome>>(() => new Set(CONTROL_OUTCOMES));
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const { from, to, ...rest } = f;
  // The route filters one outcome; with two of the three chips on, the page hides the third instead.
  const single = outcomes.size === 1 ? [...outcomes][0] : undefined;
  const filter: ControlLogFilter = { ...rest, outcome: single, from: localDayWindow(from)?.from, to: localDayWindow(to)?.to };
  const q = useQuery({ queryKey: ["control-log", filter, cursor ?? ""], queryFn: () => governance.controlLog(filter, { limit: 50, cursor }) });
  const userList = useQuery({ queryKey: ["users", "all"], queryFn: () => users.list({ limit: 200 }), enabled: can(s, "identity.user.read") });
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const set = (k: keyof DayFilter) => (e: { target: { value: string } }) => { setCursors([]); setF((p) => ({ ...p, [k]: e.target.value || undefined }) as DayFilter); };
  const toggleOutcome = (o: ControlOutcome) => { setCursors([]); setOutcomes((prev) => { const n = new Set(prev); if (n.has(o)) n.delete(o); else n.add(o); return n; }); };
  const branchName = (id: string | null | undefined) => s?.switchableBranches.find((b) => b.id === id)?.name ?? null;
  const rows = q.data?.data.items ?? [];
  const visible = single || outcomes.size === CONTROL_OUTCOMES.length ? rows : rows.filter((r) => outcomes.has(r.outcome));
  const totalCount = q.data?.data.totalCount;
  const totals = q.data?.data.totals ?? [];
  const notBuilt = q.isError && isApiError(q.error) && q.error.code === "NOT_FOUND";
  const range = `${formatDate(from)} – ${formatDate(to)}`;

  const exportCsv = async () => {
    setExporting(true);
    setNotice(null);
    try {
      const { data } = await governance.controlLogExport(filter, newIdempotencyKey());
      startDownload(data.downloadUrl, data.fileName);
      setNotice(<Alert tone="ok"><b>Export ready.</b> <span className="mono">{data.fileName}</span> · {data.rowCount} row{data.rowCount === 1 ? "" : "s"} · the download has started; <a href={data.downloadUrl} download={data.fileName}>download it again</a> until {formatTime(data.expiresAt)}. Recorded in the audit log.</Alert>);
    } catch (err) {
      setNotice(<Alert tone="crit"><b>Export failed.</b> {describeError(err)}</Alert>);
    } finally { setExporting(false); }
  };

  const shown = q.data
    ? visible.length === rows.length
      ? `${rows.length}${totalCount !== undefined ? ` of ${totalCount}` : ""} entries · newest first`
      : `${visible.length} of ${rows.length} on this page${totalCount !== undefined ? ` · ${totalCount} in range` : ""} · newest first`
    : "";

  return (
    <section className="screen" id="s-control-log">
      <div className="content">
        <PageHead
          title="Control log"
          sub={`Every PIN-authorised action · ${branchName(f.branchId) ?? (owned === null ? "all branches" : "the branches you own")} · ${range}`}
          actions={can(s, "governance.control_log.export") && <button className="btn" type="button" disabled={exporting || !online || notBuilt} onClick={() => void exportCsv()}>Export CSV<span className="small faint">recorded in the audit log</span></button>}
        />
        {notice}
        {q.data && <TotalsStrip totals={totals} />}
        <div className="toolbar">
          <TextInput type="date" aria-label="From" boxClass="w-range" value={from ?? ""} onChange={set("from")} mono />
          <TextInput type="date" aria-label="To" boxClass="w-range" value={to ?? ""} onChange={set("to")} mono />
          <Select aria-label="Action" boxClass="w-sel" value={f.action ?? ""} onChange={set("action")}>
            <option value="">Action · All</option>
            {CONTROL_LOG_ROW_ACTIONS.map((a) => <option key={a} value={a}>Action · {ACTION_LABEL[a]}</option>)}
          </Select>
          <Select aria-label="Acting user" boxClass="w-sel" value={f.actingUserId ?? ""} onChange={set("actingUserId")}>
            <option value="">User · All</option>
            {userList.data?.data.items.map((u) => <option key={u.id} value={u.id}>{u.displayName} {u.controlIdDisplay}</option>)}
          </Select>
          <Select aria-label="Branch" boxClass="w-branch" value={f.branchId ?? ""} onChange={set("branchId")}>
            <option value="">{owned === null ? "Branch · All" : "Branch · All you own"}</option>
            {branches.map((b) => <option key={b.id} value={b.id}>Branch · {b.name}</option>)}
          </Select>
          <span className="small muted">Outcome</span>
          <div className="chips">
            {CONTROL_OUTCOMES.map((o) => (
              <button key={o} type="button" className={`chip ${outcomes.has(o) ? "accent" : "neutral off"}`} aria-pressed={outcomes.has(o)} onClick={() => toggleOutcome(o)}>{outcomes.has(o) && <Icon name="check" />}{o}</button>
            ))}
          </div>
          <span className="right small faint">{shown}</span>
        </div>
        {q.isPending && <Loading what="control log" />}
        {q.isError && (isApiError(q.error) && q.error.code === "PERMISSION_DENIED" ? <NoAccessPanel page="Control log" error={q.error} />
          : notBuilt ? <Panel><Empty title="The Control log is not available yet.">The governance routes land in Sprint 1; entries are being written already and will appear here.</Empty></Panel>
          : <ErrorAlert error={q.error} />)}
        {q.data && (
          <Panel tight foot={<>
            <span className="small faint">Entries are written once and never edited or deleted · acting user, authorising Control ID, action, record, values, reason, outcome</span>
            <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!q.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, q.data!.data.nextCursor!])} />
          </>}>
            {visible.length === 0 ? <Empty title="No entries for these filters.">Every authorised, refused and locked PIN attempt is listed here once one has happened in this range.</Empty> : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>When</th><th>Action</th><th>Record</th><th>Acting user</th><th>Authorised by</th><th className="r">Value</th><th>Reason</th><th>Outcome</th></tr></thead>
                  <tbody>
                    {visible.map((r) => (
                      <tr key={r.id}>
                        <td className="mono nw">{formatDateTime(r.occurredAt)}</td>
                        <td><Chip tone={ACCENT.has(r.action) ? "accent" : "neutral"}>{actionLabel(r)}</Chip></td>
                        <td className="nw"><span className="mono">{r.documentNumber ?? <>{r.recordType} <span className="faint">{r.recordId.slice(0, 10)}</span></>}</span><RecordOf r={r} branchName={branchName} />{!f.branchId && r.branch && <div className="small faint mono">{r.branch.code}</div>}</td>
                        <td className="nw">{userCell(r.actingUser)}</td>
                        <td>{authoriserCell(r)}</td>
                        <td className="money">{valueOf(r)}</td>
                        <td>{r.reason ?? <span className="faint">—</span>}</td>
                        <td><Chip tone={r.outcome === "Authorised" ? "ok" : "crit"} dot>{r.outcome}</Chip></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
        {q.data && totals.length > 0 && <ByUser totals={totals} meta={`${branchName(f.branchId) ?? "all branches"} · ${range} · authorised actions where this user was acting`} />}
        <div className="help">{rolesSentence("governance.control_log.read")} Refused and Locked rows are evidence and are never removed.</div>
      </div>
    </section>
  );
}

const actionLabel = (r: ControlLogEntryView): string => (r.action === "Discount" && r.discountPercentBp !== null ? `Discount ${percentFromBp(r.discountPercentBp)}` : ACTION_LABEL[r.action]);

/**
 * What the record is where one document number names different records (UC-GOVERNANCE-005 step 2: each entry shows its record
 * type): a price override's SKU is the organisation's price (`product`) or one branch's (`branch_price`) — the branch its binding
 * names on a refused or locked attempt (`<branchId>:<productId>`, filed under the acting branch), else the entry's own, under which
 * an authorised branch price is filed. A receipt's price overrides all carry its GRN number: its branch prices read so, and each
 * unit price kept below its floor a unit's own price (the verification of round 3, 25 Oct 2026, R3X-2, R3S-3) — as every unit's own
 * price (`serial`) reads, wherever it was set: on the unit's page, under its serial number, and each refused or locked attempt at
 * that PIN under the unit's record, which names no document (the verification of round 5, 25 Oct 2026, V5PS-2). Null for every
 * other entry: a refused or locked PIN at a receipt (`goods_receipt`), which names no price, and every other action's.
 */
function recordOf(r: ControlLogEntryView, branchName: (id: string | null | undefined) => string | null): string | null {
  if (r.action !== "PriceOverride") return null;
  if (r.recordType === "product") return "organisation's price";
  if (r.recordType === "serial") return "a unit's own price";
  if (r.recordType !== "branch_price") return null;
  const id = r.recordId.includes(":") ? r.recordId.split(":")[0] : r.outcome === "Authorised" ? r.branch?.id : undefined;
  const name = id ? branchName(id) : null;
  // "Centurion's price", "Silver Lakes' price"; "branch price" where the branch is not one the session names.
  return name ? `${/s$/i.test(name) ? `${name}'` : `${name}'s`} price` : "branch price";
}

/**
 * `recordOf`'s words under the document; a unit's own price opens the unit, which tells a receipt's several units apart and names
 * the unit of a refused or locked attempt, whose row shows only its record.
 */
function RecordOf({ r, branchName }: { r: ControlLogEntryView; branchName: (id: string | null | undefined) => string | null }) {
  const words = recordOf(r, branchName);
  if (!words) return null;
  return <div className="small faint record-of">{r.recordType === "serial" ? <Link to={`/stock/serials/${r.recordId}`}>{words}</Link> : words}</div>;
}

function userCell(u: ControlLogEntryView["actingUser"]): ReactNode {
  if (!u) return <span className="faint">—</span>;
  return <>{u.displayName} <span className="mono">{controlIdDisplay(u.controlId)}</span></>;
}

/** Authorised: the authoriser's Control Id and name. Refused or Locked: the Control Id entered and why nobody authorised it (BR-GOVERNANCE-010). */
function authoriserCell(r: ControlLogEntryView): ReactNode {
  if (r.outcome === "Authorised" && r.authorisingUser) {
    return <><span className="mono">{controlIdDisplay(r.authorisingUser.controlId)}</span> {r.authorisingUser.displayName}{r.credentialType === "WebAuthn" && <span className="small faint"> · passkey</span>}</>;
  }
  return (
    <>
      <span className="mono">{controlIdDisplay(r.controlIdEntered)}</span> (entered)
      <div className="small faint">{r.outcome === "Locked" ? "fifth wrong PIN · locked 15 min" : r.outcome === "Refused" ? "wrong PIN or unknown Control ID" : "authoriser not on record"}</div>
    </>
  );
}

const money = (cents: number | null): ReactNode => (cents === null ? <span className="faint">—</span> : formatMoney(cents));
const signed = (cents: number | null): ReactNode => (cents === null ? <span className="faint">—</span> : cents > 0 ? `+${formatMoney(cents)}` : formatMoney(cents));
/** `R 5 300.00` without the currency mark, for the before → after line. */
const bare = (cents: number): string => formatMoney(cents).replace(/^−?R /, "");

/** The money column per action: the discount, the override and its before → after, the voided or credited amount, the signed variance. */
function valueOf(r: ControlLogEntryView): ReactNode {
  switch (r.action) {
    case "Discount": return money(r.discountValueCents);
    case "PriceOverride":
      if (r.priceBeforeCents !== null && r.priceAfterCents !== null) {
        return <>{formatMoney(Math.abs(r.priceBeforeCents - r.priceAfterCents))}<div className="small faint nw">{bare(r.priceBeforeCents)} → {bare(r.priceAfterCents)}</div></>;
      }
      // A tenant price cleared under the PIN (UC-PRICING-001 A2), or a branch price set where there was none to charge (UC-PRICING-002).
      if (r.priceBeforeCents !== null) return <>{formatMoney(r.priceBeforeCents)}<div className="small faint nw">{bare(r.priceBeforeCents)} → no price</div></>;
      if (r.priceAfterCents !== null) return <>{formatMoney(r.priceAfterCents)}<div className="small faint nw">no price → {bare(r.priceAfterCents)}</div></>;
      return money(null);
    case "Void": case "CreditNote": case "Refund": case "InvoiceEdit": case "Delete": return money(r.priceBeforeCents);
    case "StockTakeFinalise":
      // An opening's posting ended before its last part (UC-INVENTORY-010 A8): the one StockTakeFinalise row with a value before and
      // after — the opening's base valuation before and after the end. Its amount is stock that never came in: unsigned, never the
      // '+' of a stock-take variance or the '−' of a shortfall to look for (UC-GOVERNANCE-005; the third review, SPE-1).
      if (r.priceBeforeCents !== null && r.priceAfterCents !== null) {
        return <>{formatMoney(r.priceBeforeCents - r.priceAfterCents)} not posted<div className="small faint nw">opening {bare(r.priceBeforeCents)} → {bare(r.priceAfterCents)}</div></>;
      }
      return signed(r.varianceAmountCents);
    case "CashUpVariance": case "StockAdjustment": return signed(r.varianceAmountCents);
    default: return money(r.varianceAmountCents ?? r.discountValueCents ?? r.priceBeforeCents);
  }
}

const sumOf = (totals: ControlLogTotalsLine[], k: "discounts" | "voids" | "creditNotes" | "refunds") =>
  totals.reduce((acc, l) => ({ count: acc.count + l[k].count, valueCents: acc.valueCents + l[k].valueCents }), { count: 0, valueCents: 0 });

/** The prototype's strip from the per-user totals: authorised money by kind, and the refused or locked attempts. */
function TotalsStrip({ totals }: { totals: ControlLogTotalsLine[] }) {
  const d = sumOf(totals, "discounts"), v = sumOf(totals, "voids"), c = sumOf(totals, "creditNotes"), rf = sumOf(totals, "refunds");
  const refused = totals.reduce((n, l) => n + l.refused, 0);
  const worst = totals.reduce<ControlLogTotalsLine | null>((best, l) => (l.refused > (best?.refused ?? 0) ? l : best), null);
  return (
    <div className="stats">
      <div className="stat"><span className="v">{d.count}</span><span className="l">Discounts authorised</span><span className="d">{formatMoney(d.valueCents)}</span></div>
      <div className="stat"><span className="v">{v.count}</span><span className="l">Voids</span><span className="d">{formatMoney(v.valueCents)}</span></div>
      <div className="stat"><span className="v">{c.count}</span><span className="l">Credit notes</span><span className="d">{formatMoney(c.valueCents)}</span></div>
      <div className="stat"><span className="v">{rf.count}</span><span className="l">Refunds</span><span className="d">{formatMoney(rf.valueCents)}</span></div>
      <div className="stat"><span className="v">{refused}</span><span className="l">Refused PINs</span><span className={`d${refused > 0 ? " down" : ""}`}>{worst?.user ? `${worst.user.displayName} · ${worst.refused} attempt${worst.refused === 1 ? "" : "s"}` : "none in this range"}</span></div>
    </div>
  );
}

/** The by-user table of the prototype: discounts, their value, voids, credit notes and refused attempts per acting user. */
function ByUser({ totals, meta }: { totals: ControlLogTotalsLine[]; meta: string }) {
  const all = { discounts: sumOf(totals, "discounts"), voids: sumOf(totals, "voids"), creditNotes: sumOf(totals, "creditNotes"), refused: totals.reduce((n, l) => n + l.refused, 0) };
  return (
    <Panel title="By user" meta={meta} tight>
      <table>
        <thead><tr><th>User</th><th className="r">Discounts</th><th className="r">Value</th><th className="r">Voids</th><th className="r">Credit notes</th><th className="r">Refused</th></tr></thead>
        <tbody>
          {totals.map((l) => {
            const share = all.discounts.count >= SHARE_FLAG_MIN_DISCOUNTS ? l.discounts.count / all.discounts.count : 0;
            return (
              <tr key={l.userId}>
                <td>
                  <div className="stack gap-4">
                    <span className="nw">{l.user ? <>{l.user.displayName} <span className="mono">{controlIdDisplay(l.user.controlId)}</span></> : <span className="mono">{l.userId.slice(0, 8)}</span>}</span>
                    {share > 0.5 && <div><Chip tone="warn">{Math.round(share * 100)}% of discounts</Chip></div>}
                  </div>
                </td>
                <td className="r num">{l.discounts.count}</td>
                <td className="money">{formatMoney(l.discounts.valueCents)}</td>
                <td className="r num">{l.voids.count}</td>
                <td className="r num">{l.creditNotes.count}</td>
                <td className="r">{l.refused > 0 ? <Chip tone="crit">{l.refused}</Chip> : <span className="num">0</span>}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot><tr><td>Total</td><td className="r num">{all.discounts.count}</td><td className="money">{formatMoney(all.discounts.valueCents)}</td><td className="r num">{all.voids.count}</td><td className="r num">{all.creditNotes.count}</td><td className="r num">{all.refused}</td></tr></tfoot>
      </table>
    </Panel>
  );
}
